// app.config.ts

import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, Router, withEnabledBlockingInitialNavigation } from '@angular/router';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';

import { routes } from './app.crowndesk.routes';

import { environment } from '@environments';
import { impersonationInterceptor } from '@crowndesk-core/login-as/impersonation.interceptor';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import '@progress/kendo-angular-intl/locales/nl-BE/all';
import { TranslateLoader, TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import * as Sentry from '@sentry/angular';

import localeNlBe from '@angular/common/locales/nl-BE';

import { DOCUMENT, registerLocaleData } from '@angular/common';

import { firstValueFrom } from 'rxjs';

import {
  CapitalizePipe,
  FormErrorService,
  IS_CROWNDESK,
  provideKendoLocalization,
  provideSharedInterceptors,
  SHARED_ENVIRONMENT_CONFIG,
  TranslationLoaderService,
} from 'shared';
import { authHttpInterceptorFn, provideAuth0 } from '@auth0/auth0-angular';

const isSentryEnabled = !isDevMode() && location.hostname !== 'localhost';
// ✅ Register Dutch locale
registerLocaleData(localeNlBe, 'nl-BE');

// ✅ Translate loader factory
export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

// ✅ Async initializer
export async function appInitializerFactory() {
  const translate = inject(TranslateService);
  const loaderService = inject(TranslationLoaderService);

  try {
    const results = await Promise.allSettled([firstValueFrom(translate.use('nl'))]);
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        console.error(`Initializer Task ${index + 1} failed:`, result.reason);
      }
    });
  } catch (err) {
    console.error('App initialization error:', err);
  } finally {
    loaderService.setLoading(false);
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    ...(environment.auth0
      ? [
          provideAuth0({
            domain: environment.auth0.domain,
            clientId: environment.auth0.clientId,
            authorizationParams: environment.auth0.authorizationParams,
            useRefreshTokens: environment.auth0.useRefreshTokens,
            cacheLocation: 'localstorage',
            httpInterceptor: environment.auth0.httpInterceptor,
          }),
        ]
      : []),
    provideHttpClient(
      withInterceptors([
        // Custom HTTP interceptors from shared lib
        ...provideSharedInterceptors(),
        impersonationInterceptor,
        authHttpInterceptorFn,
      ]),
    ),

    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      }),
    ),
    importProvidersFrom(TranslateModule),
    FormErrorService,
    TranslatePipe,
    CapitalizePipe,
    provideHttpClient(),
    provideAnimations(),
    provideAnimationsAsync(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withEnabledBlockingInitialNavigation()),

    // Localization
    provideKendoLocalization(),

    // Disable scroll-to-change on all kendo-numerictextbox elements globally
    provideAppInitializer(() => {
      const doc = inject(DOCUMENT);
      doc.addEventListener(
        'wheel',
        (e: WheelEvent) => {
          if ((e.target as Element)?.closest('kendo-numerictextbox')) {
            e.stopImmediatePropagation();
          }
        },
        { capture: true },
      );
    }),

    // Global env injection
    { provide: SHARED_ENVIRONMENT_CONFIG, useValue: environment },
    { provide: IS_CROWNDESK, useValue: true },

    // Async translation loader
    provideAppInitializer(appInitializerFactory),

    ...(isSentryEnabled
      ? [
          { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
          { provide: Sentry.TraceService, deps: [Router] },
        ]
      : []),
    { provide: LOCALE_ID, useValue: 'nl-BE' },
  ],
};
